/**
 * Polyfills for IE 11
 */
import 'core-js/es/array' // Array.prototype.includes
import 'core-js/es/object' // Object.values
import 'core-js/web/url' // new URL()

import React from 'react'
import ReactDOM from 'react-dom'
import { createStore, applyMiddleware } from 'redux'
import thunk from 'redux-thunk'
import { Provider } from 'react-redux'
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom'

import { Middleware as RedirectMiddleware } from '@myxplor/notifications-redux'

import '@myxplor/ui/build/main.css'
import '@myxplor/forms/build/main.css'
import '@myxplor/icons/build/main.css'

import { IS_EMBEDDED, IS_PRODADMIN_EMBEDDED, setBodyClass } from './embedded'
import RootReducer from './reducer'
import * as Analytics from './helpers/analytics'

import Authorisation from './authorisation'
import Onboarding from './onboarding'
import CareEnrolments from './care-enrolments'
import GovRegistrations from './gov-registrations'
import ProvidersRegistrations, { ProviderRegistrationOnBoarding } from './providers-registrations'
import Personnel from './personnel'
import IssCases from './iss-cases'
import ISSCaseClaims from './iss-case-claims'
import SessionSummaries from './session-summaries'
import Export from './export'
import Payments from './payments'
import ParentAgreement from './parent-agreement'
import { DisplayAsset } from './ui/gov-messages'
import { startOfWeek } from './week'
import Reports from './reports'
import ProviderLevelReports from './provider-level/reports'
import { AppLayout, AuthorisedLayout } from './layout'
import RegisterNew from './register-new'
import ISSCredentialsUpdate from './iss-credentials-update'
import OffboardingSubmissions from './offboarding'

import './rebates.css'

const store = createStore(RootReducer, applyMiddleware(thunk, RedirectMiddleware))

setBodyClass(IS_EMBEDDED)

Analytics.initialise()

ReactDOM.render(
  <Provider store={store}>
    <Router>
      <Authorisation isEmbedded={IS_EMBEDDED} isProdAdminEmbedded={IS_PRODADMIN_EMBEDDED}>
        <Switch>
          <Route
            path="/parents/:parent_id/care-enrolments/:care_id"
            render={({ match }) => (
              <ParentAgreement
                parent_id={match.params.parent_id}
                care_id={match.params.care_id}
                embedded={IS_EMBEDDED}
              />
            )}
          />

          <AppLayout isEmbedded={IS_EMBEDDED}>
            <Switch>
              <Route
                path="/view-asset/:path*"
                render={({ match }) => <DisplayAsset path={match.params.path} />}
              />

              <Route
                render={({ match }) => (
                  <AuthorisedLayout location={match.location} isEmbedded={IS_EMBEDDED}>
                    <Switch>
                      <Route
                        path="/register/(services||providers)/:id"
                        render={({ match }) => (
                          <Onboarding
                            record_type={match.params['0']}
                            id={match.params.id}
                            base_url={match.url}
                          />
                        )}
                      />

                      <Route exact path="/export" component={Export} />

                      <Route path="/reports" render={() => <Reports embedded={IS_EMBEDDED} />} />

                      <Route
                        path="/providers/:gov_id/provider-reports"
                        render={({ match }) => (
                          <ProviderLevelReports
                            gov_id={match.params.gov_id}
                            embedded={IS_EMBEDDED}
                          />
                        )}
                      />

                      <Route
                        path="/(services||providers)/:gov_id/reports"
                        render={({ match }) => (
                          <Reports
                            record_type={match.params['0']}
                            gov_id={match.params.gov_id}
                            embedded={IS_EMBEDDED}
                          />
                        )}
                      />

                      <Route
                        path="/(services||providers)/:id/submissions/:start_date?"
                        render={({ match }) => (
                          <SessionSummaries
                            embedded={IS_EMBEDDED}
                            record_type={match.params['0']}
                            id={match.params.id}
                            base_url={`/${match.params['0']}/${match.params.id}/submissions`}
                            start_date={startOfWeek(match.params.start_date)}
                          />
                        )}
                      />

                      <Route
                        path="/(services||providers)/:id/payments/:start_date?"
                        render={({ match }) => (
                          <Payments
                            embedded={IS_EMBEDDED}
                            record_type={match.params['0']}
                            id={match.params.id}
                            base_url={`/${match.params['0']}/${match.params.id}/payments`}
                            start_date={startOfWeek(match.params.start_date)}
                          />
                        )}
                      />

                      <Route
                        path="/(services||providers)/:id/enrolments"
                        render={({ match }) => (
                          <CareEnrolments
                            embedded={IS_EMBEDDED}
                            record_type={match.params['0']}
                            id={match.params.id}
                            base_url={match.url}
                          />
                        )}
                      />

                      <Route
                        path="/(services||providers)/:id/personnel"
                        render={({ match }) => (
                          <Personnel
                            embedded={IS_EMBEDDED}
                            record_type={match.params['0']}
                            id={match.params.id}
                            base_url={match.url}
                          />
                        )}
                      />

                      <Route
                        path="/providers/:id/registrations/new"
                        render={({ match }) => (
                          <ProviderRegistrationOnBoarding
                            id={match.params.id}
                            embedded={IS_EMBEDDED}
                          />
                        )}
                      />

                      <Route
                        path="/providers/:id/registrations"
                        render={({ match, location }) => (
                          <ProvidersRegistrations
                            id={match.params.id}
                            base_url={match.url}
                            location={location}
                            embedded={IS_EMBEDDED}
                          />
                        )}
                      />

                      <Route
                        path="/(services||providers)/:id/iss-cases"
                        render={({ match }) => {
                          return (
                            <IssCases
                              record_type={match.params['0']}
                              id={match.params.id}
                              base_url={match.url}
                              embedded={IS_EMBEDDED}
                            />
                          )
                        }}
                      />

                      <Route
                        path="/(services||providers)/:id/iss-case-claims/:start_date?"
                        render={({ match }) => {
                          return (
                            <ISSCaseClaims
                              record_type={match.params['0']}
                              id={match.params.id}
                              base_url={`/${match.params['0']}/${match.params.id}/iss-case-claims`}
                              start_date={startOfWeek(match.params.start_date)}
                              embedded={IS_EMBEDDED}
                            />
                          )
                        }}
                      />

                      <Route
                        exact path="/registration_holders/new"
                        render={() =>
                          <RegisterNew
                            base_url={match.url}
                            embedded={IS_EMBEDDED}
                          />}
                      />

                      <Route
                        exact path="/iss_credentials/update"
                        render={() =>
                          <ISSCredentialsUpdate
                            base_url={match.url}
                            embedded={IS_EMBEDDED}
                          />}
                      />

                      <Route
                        path="/gov-registrations"
                        render={({ match, location }) => (
                          <GovRegistrations
                            base_url={match.url}
                            location={location}
                            embedded={IS_EMBEDDED}
                          />
                        )}
                      />

                      <Route
                        path="/offboarding/submissions"
                        render={ () => 
                          <OffboardingSubmissions
                            base_url={match.url}
                            embedded={IS_EMBEDDED}
                          />  
                        }
                      />

                      <Redirect to="/gov-registrations" />
                    </Switch>
                  </AuthorisedLayout>
                )}
              />
            </Switch>
          </AppLayout>
        </Switch>
      </Authorisation>
    </Router>
  </Provider>,
  document.getElementById('root')
)



// WEBPACK FOOTER //
// ./src/index.js